import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaCheckCircle, FaUser, FaBuilding, FaWhatsapp, FaExclamationTriangle } from 'react-icons/fa';
import { theme } from '../styles/theme';
import { config } from '../config/environment';
import { useCurrentUser } from '../hooks/useCurrentUser';

const CallbackContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: ${theme.spacing[4]};
  background: ${theme.colors.backgroundSecondary};
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);
  padding: 32px;
  max-width: 800px;
  margin: 0 auto;
`;

const StatusText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Card = styled.div`
  background: ${theme.colors.backgroundTertiary};
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  padding: 20px;
  margin-top: 16px;
  width: 100%;
  max-width: 600px;
  text-align: left;
`;

const ErrorCard = styled(Card)`
  border: 2px solid ${theme.colors.error};
  background: ${theme.colors.backgroundSecondary};
`;

const decodeGoogleIdFromState = (stateParam: string | null): string | null => {
  if (!stateParam) return null;

  try {
    const [payloadEncoded] = stateParam.split('.');
    if (!payloadEncoded) return null;

    const base64 = payloadEncoded.replace(/-/g, '+').replace(/_/g, '/');
    const padding = (4 - (base64.length % 4)) % 4;
    const padded = base64 + '='.repeat(padding);
    const decoded = atob(padded);

    const [googleId] = decoded.split('|');
    return googleId || null;
  } catch (error) {
    console.warn('[CALLBACK] Falha ao decodificar state do Facebook OAuth', error);
    return null;
  }
};

const SettingsCallback: React.FC = () => {
  const { user: googleUser } = useCurrentUser();
  const [status, setStatus] = useState('Aguardando autenticação...');
  const [user, setUser] = useState<any>(null);
  const [business, setBusiness] = useState<any>(null);
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'auth' | 'select' | 'whatsapp'>(() => {
    return localStorage.getItem('onboardingStep') as 'auth' | 'select' | 'whatsapp' || 'auth';
  });
  const [resolvedGoogleId, setResolvedGoogleId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Busca dados do localStorage se já existem
    const userJson = localStorage.getItem('facebookUser');
    const businessJson = localStorage.getItem('facebookBusiness');
    const selectedBusinessJson = localStorage.getItem('selectedBusiness');
    const onboardingStep = localStorage.getItem('onboardingStep');
    const onboardingComplete = localStorage.getItem('onboardingComplete');
    const cachedGoogleId = localStorage.getItem('oauthGoogleId');

    if (cachedGoogleId && cachedGoogleId !== 'undefined' && cachedGoogleId !== resolvedGoogleId) {
      setResolvedGoogleId(cachedGoogleId);
    }

    if (userJson && userJson !== 'undefined') {
      try {
        setUser(JSON.parse(userJson));
      } catch (e) {
        console.warn('Failed to parse facebookUser from localStorage');
      }
    }
    if (businessJson && businessJson !== 'undefined') {
      try {
        setBusiness(JSON.parse(businessJson));
      } catch (e) {
        console.warn('Failed to parse facebookBusiness from localStorage');
      }
    }
    if (selectedBusinessJson && selectedBusinessJson !== 'undefined') {
      try {
        setSelectedBusiness(JSON.parse(selectedBusinessJson));
      } catch (e) {
        console.warn('Failed to parse selectedBusiness from localStorage');
      }
    }

    // Se onboarding já foi finalizado, pula para o final
    if (onboardingComplete === 'true') {
      setStep('whatsapp');
      setStatus('Onboarding finalizado!');
      return;
    }

    // Se não tem dados, faz autenticação
    if (!userJson || !businessJson) {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const stateParam = params.get('state');
      const decodedGoogleId = decodeGoogleIdFromState(stateParam);
      const googleId = decodedGoogleId || googleUser?.uid || null;

      if (googleId) {
        setResolvedGoogleId(googleId);
        localStorage.setItem('oauthGoogleId', googleId);
      }

      console.log('[CALLBACK] URL params:', {
        code: code?.substring(0, 20) + '...',
        state: stateParam,
        googleUserUid: googleUser?.uid,
        decodedGoogleId,
      });

      if (!code) {
        setError('Código de autorização não encontrado na URL');
        setStatus('❌ Erro no callback do Facebook');
        return;
      }

      if (!googleId) {
        setError('googleId não encontrado no state parameter. Faça login com Google e tente novamente.');
        setStatus('❌ Usuário Google não identificado');
        return;
      }

      console.log('[CALLBACK] Autenticando com googleId:', googleId);
      setStatus('🔐 Autenticando com Facebook...');

      // Envia code + googleId para o backend
      const paramsEncoded = new URLSearchParams();
      paramsEncoded.set('code', code);
      paramsEncoded.set('googleId', googleId);

      if (stateParam) {
        paramsEncoded.append('state', stateParam);
      }

      fetch(`${config.apiUrl}/api/facebook/callback?${paramsEncoded.toString()}`)
        .then(res => res.json())
        .then(data => {
          // Verifica se há erro do backend
          if (data.error || data.message?.includes('Erro') || data.message?.includes('erro')) {
            setError(data.error || data.message || 'Erro desconhecido na autenticação');
            setStatus('❌ Erro ao autenticar com Facebook');
            return;
          }

          if (data.accessToken) {
            setStatus('✅ Autenticado com sucesso!');
            setError(null);

            let userObj;
            try {
              userObj = typeof data.userJson === 'string' ? JSON.parse(data.userJson) : data.userJson;
              setUser(userObj);
              localStorage.setItem('facebookUser', JSON.stringify(userObj));
            } catch {
              console.warn('Erro ao parsear userJson, usando valor bruto');
              userObj = data.userJson;
              setUser(data.userJson);
              localStorage.setItem('facebookUser', JSON.stringify(data.userJson));
            }

            try {
              const businessObj = typeof data.businessJson === 'string' ? JSON.parse(data.businessJson) : data.businessJson;
              setBusiness(businessObj);
              localStorage.setItem('facebookBusiness', JSON.stringify(businessObj));
            } catch {
              console.warn('Erro ao parsear businessJson, usando valor bruto');
              setBusiness(data.businessJson);
              localStorage.setItem('facebookBusiness', JSON.stringify(data.businessJson));
            }

            setStep('select');
            localStorage.setItem('onboardingStep', 'select');
          } else {
            setError('Access token não recebido do servidor');
            setStatus('❌ Erro na autenticação');
          }
        })
        .catch(err => {
          console.error('Erro no fetch do callback:', err);
          setError('Erro de conexão com o servidor: ' + err.message);
          setStatus('❌ Erro na autenticação');
        });
    } else {
      setStatus('Autenticado com sucesso!');
      if (onboardingStep) setStep(onboardingStep as 'auth' | 'select' | 'whatsapp');
      else setStep('select');
    }
  }, [navigate, googleUser?.uid, resolvedGoogleId]);

  // Salva seleção do business
  const handleSelectBusiness = (id: string) => {
    const selected = business?.data?.find((b: any) => b.id === id);
    setSelectedBusiness(selected);
    localStorage.setItem('selectedBusiness', JSON.stringify(selected));
    setStep('whatsapp');
    localStorage.setItem('onboardingStep', 'whatsapp');
  };

  // Progresso visual minimalista
  const renderProgress = () => {
    const steps = [
      { key: 'auth', label: 'Autenticação', icon: FaUser },
      { key: 'select', label: 'Negócio', icon: FaBuilding },
      { key: 'whatsapp', label: 'WhatsApp', icon: FaWhatsapp },
    ];

    const currentStepIndex = steps.findIndex(s => s.key === step);

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        marginBottom: 32,
        padding: '12px 20px',
        background: theme.colors.backgroundTertiary,
        borderRadius: theme.borders.radius.lg,
        border: `1px solid ${theme.colors.border}`,
      }}>
        {steps.map((s, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = s.key === step;
          const isPending = index > currentStepIndex;

          return (
            <React.Fragment key={s.key}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                color: isCompleted ? theme.colors.success : isCurrent ? theme.colors.primary : theme.colors.textTertiary,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: isCurrent ? 600 : 400,
              }}>
                {isCompleted ? FaCheckCircle({ size: 18 }) : s.icon({ size: 18 })}
                <span>{s.label}</span>
              </div>
              {index < steps.length - 1 && (
                <div style={{
                  width: 32,
                  height: 2,
                  background: isCompleted ? theme.colors.success : theme.colors.border,
                  borderRadius: 2,
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  // Seleção de negócio
  const renderBusinessSelector = () => (
    <Card style={{ maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        {FaBuilding({ color: theme.colors.primary, size: 24 })}
        <div>
          <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.lg, fontWeight: 600, color: theme.colors.textPrimary }}>
            Selecione seu Negócio
          </h3>
          <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
            Escolha qual Business Manager conectar ao WhatsApp
          </p>
        </div>
      </div>
      <select
        style={{
          width: '100%',
          padding: '12px 16px',
          borderRadius: theme.borders.radius.md,
          fontSize: theme.typography.fontSize.base,
          backgroundColor: theme.colors.backgroundTertiary,
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.border}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          outline: 'none',
        }}
        onChange={e => handleSelectBusiness(e.target.value)}
        defaultValue=""
        onFocus={(e) => e.currentTarget.style.borderColor = theme.colors.primary}
        onBlur={(e) => e.currentTarget.style.borderColor = theme.colors.border}
      >
        <option value="" disabled>Selecione uma opção...</option>
        {business?.data?.map((b: any) => (
          <option key={b.id} value={b.id}>{b.name}</option>
        ))}
      </select>
      {business?.data && business.data.length === 0 && (
        <p style={{ marginTop: 12, fontSize: theme.typography.fontSize.sm, color: theme.colors.warning }}>
          ⚠️ Nenhum negócio encontrado. Crie um no Facebook Business Manager.
        </p>
      )}
    </Card>
  );

  // Estado para status da conexão WhatsApp
  const [waStatus, setWaStatus] = useState<string | null>(null);
  const [waLoading, setWaLoading] = useState(false);
  const [setupUrl, setSetupUrl] = useState<string | null>(null);
  const [instructions, setInstructions] = useState<string[] | null>(null);
  const [autoRetryEnabled, setAutoRetryEnabled] = useState(false);

  // Chama backend para conectar WhatsApp Business (Multi-Tenant)
  const connectWhatsAppBusiness = async () => {
    const googleId = resolvedGoogleId || googleUser?.uid;

    if (!googleId) {
      setError('Usuário Google não identificado. Faça login novamente.');
      return;
    }

    setWaLoading(true);
    setWaStatus(null);
    setError(null);

    try {
      const res = await fetch(`${config.apiUrl}/api/v1/whatsapp/send/connect-business`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          googleId, // MULTI-TENANT: ID do Google do usuário
          businessId: selectedBusiness?.id,
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setWaStatus(`✅ ${data.message || 'WhatsApp Business conectado!'}`);
        if (data.phoneNumber) {
          setWaStatus(prev => `${prev}\n📱 Número: ${data.phoneNumber}`);
        }
        if (data.verifiedName) {
          setWaStatus(prev => `${prev}\n✓ ${data.verifiedName}`);
        }
        localStorage.setItem('onboardingComplete', 'true');
        setError(null);
        setSetupUrl(null);
        setInstructions(null);
      } else if (data.needsPhoneNumber) {
        // Caso especial: precisa configurar número
        setWaStatus('⚠️ Configuração de Número Necessária');
        setError(null);
        setSetupUrl(data.setupUrl);
        setInstructions(data.instructions || []);
      } else {
        const errorMsg = data.error || data.message || 'Erro desconhecido';
        const hint = data.hint || '';
        setError(`${errorMsg}\n\n💡 ${hint}`);
        setWaStatus('❌ Falha na conexão');
        setSetupUrl(null);
        setInstructions(null);
      }
    } catch (err: any) {
      console.error('Erro ao conectar WhatsApp Business:', err);
      setError('Erro de conexão com o servidor: ' + err.message);
      setWaStatus('❌ Erro de conexão');
    }

    setWaLoading(false);
  };

  // Auto-verificação quando usuário volta do Facebook (após configurar número)
  React.useEffect(() => {
    if (!setupUrl || !autoRetryEnabled || !selectedBusiness?.id) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[AUTO-RETRY] Usuário voltou para a página, verificando número...');
        // Aguarda 2 segundos para dar tempo do Facebook processar
        setTimeout(() => {
          connectWhatsAppBusiness();
        }, 2000);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [setupUrl, autoRetryEnabled, selectedBusiness]);

  // Etapa guiada WhatsApp Business API
  const renderWhatsAppSetup = () => {
    // Detecta ambiente de teste (sandbox)
    const isSandbox = selectedBusiness?.name?.toLowerCase().includes('sandbox') || selectedBusiness?.name?.toLowerCase().includes('teste') || selectedBusiness?.id?.startsWith('test');

    if (isSandbox) {
      return (
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {FaWhatsapp({ color: theme.colors.info, size: 22 })}
            <strong>Ambiente de Teste WhatsApp Business API</strong>
          </div>
          <div style={{ marginTop: 12 }}>
            <p>Você está usando o número de teste do Meta. Não é necessário vincular número real.</p>
            <p>Use o painel do Meta para enviar e receber mensagens de teste.</p>
            <button
              style={{
                marginTop: 18,
                background: theme.colors.success,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 16,
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                transition: 'background 0.2s',
              }}
              onClick={() => {
                localStorage.setItem('onboardingComplete', 'true');
                setStatus('Onboarding finalizado!');
              }}
            >Avançar para testes</button>
          </div>
        </Card>
      );
    }

    // Checklist visual para produção
    const checklist = [
      {
        label: 'Número de WhatsApp Business vinculado ao negócio',
        link: 'https://business.facebook.com/settings/whatsapp-business-accounts/',
        status: waStatus && waStatus.startsWith('success') ? true : false
      },
      {
        label: 'Permissões e escopos corretos no Facebook Developers',
        link: 'https://developers.facebook.com/apps/',
        status: waStatus && waStatus.startsWith('success') ? true : false
      },
      {
        label: 'ID correto da conta WhatsApp Business',
        link: 'https://business.facebook.com/settings/',
        status: waStatus && waStatus.startsWith('success') ? true : false
      }
    ];

    // Mensagem de erro detalhada
    let errorMsg = '';
    if (waStatus && waStatus.startsWith('Erro')) {
      if (waStatus.includes('nonexisting field')) {
        errorMsg = 'O negócio selecionado não possui número de WhatsApp Business vinculado. Acesse o Facebook Business Manager, vincule o número e clique em "Verificar novamente".';
      } else if (waStatus.includes('OAuthException')) {
        errorMsg = 'Permissões insuficientes. Verifique se o app do Facebook Developers tem os escopos corretos e o usuário está autorizado.';
      } else {
        errorMsg = 'Erro ao conectar. Revise os dados e tente novamente.';
      }
    }

    return (
      <Card style={{ maxWidth: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          {FaWhatsapp({ color: theme.colors.success, size: 24 })}
          <div>
            <h3 style={{ margin: 0, fontSize: theme.typography.fontSize.lg, fontWeight: 600, color: theme.colors.textPrimary }}>
              Conectar WhatsApp Business
            </h3>
            <p style={{ margin: '4px 0 0 0', fontSize: theme.typography.fontSize.sm, color: theme.colors.textSecondary }}>
              {selectedBusiness?.name}
            </p>
          </div>
        </div>

        <button
          style={{
            width: '100%',
            marginTop: 0,
            background: waLoading ? theme.colors.backgroundSecondary : theme.colors.primary,
            color: '#fff',
            border: 'none',
            borderRadius: theme.borders.radius.md,
            padding: '14px 24px',
            fontSize: theme.typography.fontSize.base,
            fontWeight: 600,
            cursor: waLoading ? 'not-allowed' : 'pointer',
            boxShadow: theme.shadows.md,
            transition: 'all 0.2s ease',
          }}
          disabled={waLoading}
          onClick={connectWhatsAppBusiness}
          onMouseEnter={(e) => {
            if (!waLoading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = theme.shadows.lg;
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = theme.shadows.md;
          }}
        >
          {waLoading ? '⏳ Conectando...' : '🔗 Conectar WhatsApp Business'}
        </button>

        {/* Sucesso */}
        {waStatus && waStatus.includes('✅') && (
          <div style={{
            marginTop: 16,
            padding: '16px',
            background: `${theme.colors.success}20`,
            border: `1px solid ${theme.colors.success}`,
            borderRadius: theme.borders.radius.md,
            color: theme.colors.textPrimary,
            fontSize: theme.typography.fontSize.sm,
            lineHeight: 1.6,
            whiteSpace: 'pre-line',
          }}>
            {waStatus}
          </div>
        )}

        {/* Precisa configurar número - CARD VERMELHO */}
        {setupUrl && instructions && (
          <div style={{
            marginTop: 16,
            padding: '16px',
            background: `${theme.colors.error}15`,
            border: `2px solid ${theme.colors.error}`,
            borderRadius: theme.borders.radius.md,
            color: theme.colors.textPrimary,
            fontSize: theme.typography.fontSize.sm,
          }}>
            <strong style={{ color: theme.colors.error, display: 'block', marginBottom: 12, fontSize: theme.typography.fontSize.base }}>
              ❌ Ação Necessária: Adicionar Número de Telefone
            </strong>
            <p style={{ margin: '0 0 12px 0', lineHeight: 1.6 }}>
              A conta WhatsApp Business foi encontrada, mas <strong>não há número de telefone vinculado</strong>.
              Siga os passos abaixo:
            </p>
            <ol style={{ margin: '0 0 16px 0', paddingLeft: 20, lineHeight: 1.8 }}>
              {instructions.map((instruction, idx) => (
                <li key={idx}>{instruction}</li>
              ))}
            </ol>
            <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
              <button
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: theme.colors.error,
                  color: '#fff',
                  border: 'none',
                  borderRadius: theme.borders.radius.md,
                  fontSize: theme.typography.fontSize.base,
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: theme.shadows.md,
                  transition: 'all 0.2s ease',
                }}
                onClick={() => {
                  setAutoRetryEnabled(true); // Ativa auto-verificação
                  window.open(setupUrl, '_blank');
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = theme.shadows.lg;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = theme.shadows.md;
                }}
              >
                📱 Configurar Número no Facebook →
              </button>
              <button
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'transparent',
                  color: theme.colors.primary,
                  border: `1px solid ${theme.colors.primary}`,
                  borderRadius: theme.borders.radius.md,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: 600,
                  cursor: waLoading ? 'not-allowed' : 'pointer',
                  opacity: waLoading ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                }}
                disabled={waLoading}
                onClick={connectWhatsAppBusiness}
                onMouseEnter={(e) => {
                  if (!waLoading) {
                    e.currentTarget.style.background = `${theme.colors.primary}15`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                {waLoading ? '⏳ Verificando...' : '🔄 Já Configurei - Verificar Novamente'}
              </button>
            </div>
          </div>
        )}

        {/* Erro */}
        {errorMsg && !setupUrl && (
          <div style={{
            marginTop: 16,
            padding: '16px',
            background: `${theme.colors.error}20`,
            border: `1px solid ${theme.colors.error}`,
            borderRadius: theme.borders.radius.md,
            color: theme.colors.textPrimary,
            fontSize: theme.typography.fontSize.sm,
            lineHeight: 1.6,
            whiteSpace: 'pre-line',
          }}>
            <strong style={{ color: theme.colors.error, display: 'block', marginBottom: 8 }}>
              ⚠️ Erro na Conexão
            </strong>
            {errorMsg}
          </div>
        )}
      </Card>
    );
  };

  return (
    <CallbackContainer>
      <h2 style={{ color: theme.colors.primary, marginBottom: 4, fontSize: theme.typography.fontSize['3xl'] }}>
        Integração WhatsApp Business
      </h2>
      <p style={{ color: theme.colors.textTertiary, fontSize: theme.typography.fontSize.sm, marginBottom: 24 }}>
        Configure sua conta de forma rápida e segura
      </p>

      {renderProgress()}

      {/* Card de Erro */}
      {error && (
        <ErrorCard>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            {FaExclamationTriangle({ color: theme.colors.error, size: 24 })}
            <div style={{ flex: 1 }}>
              <strong style={{ color: theme.colors.error, display: 'block', marginBottom: 8 }}>
                Erro na Integração
              </strong>
              <p style={{ color: theme.colors.textSecondary, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                {error}
              </p>
            </div>
          </div>
        </ErrorCard>
      )}

      {/* Seletor de Negócio */}
      {step === 'select' && business && business.data && renderBusinessSelector()}

      {/* Configuração WhatsApp */}
      {step === 'whatsapp' && selectedBusiness && renderWhatsAppSetup()}
    </CallbackContainer>
  );
};

export default SettingsCallback;
